/**
 * ConversionSettings Component
 *
 * Allows users to select output format and quality options.
 */

import { memo } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { isLocalMode } from '../../utils/apiMode';
import { getFormatOptions, getQualityOptions, type ConversionQuality } from './formats';

// ============================================================================
// Types
// ============================================================================

export interface ConversionSettingsData {
  outputFormat: string;
  quality: ConversionQuality;
  preserveMetadata: boolean;
  normalizeAudio: boolean;
}

interface ConversionSettingsProps {
  settings: ConversionSettingsData;
  onChange: (settings: ConversionSettingsData) => void;
  mediaType: 'audio' | 'video';
  /**
   * Extension of the selected input file (e.g. ".mp4"), lowercase with the dot.
   * Used to label the same-format entry as a recompression instead of letting
   * it look like a real format change. Empty in batch mode (mixed inputs).
   */
  inputExtension: string;
}

// ============================================================================
// Component
// ============================================================================

export const ConversionSettings = memo(function ConversionSettings({
  settings,
  onChange,
  mediaType,
  inputExtension,
}: ConversionSettingsProps) {
  const { t } = useTranslation();
  const formats = getFormatOptions(mediaType);
  const qualityOptions = getQualityOptions(mediaType);
  const isSameFormat = inputExtension !== '' && settings.outputFormat === inputExtension;

  return (
    <div className="p-4 sm:p-6 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700">
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">
        {t('converter.settings.title', { defaultValue: 'Conversion Settings' })}
      </h3>

      {/* Output Format */}
      <div className="mb-6">
        <label className="label-themed">
          {t('converter.settings.outputFormat', { defaultValue: 'Output Format' })}
        </label>
        <select
          value={settings.outputFormat}
          onChange={(e) => onChange({ ...settings, outputFormat: e.target.value })}
          className="w-full p-3 rounded-lg border border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {formats.map((format) => (
            <option key={format.value} value={format.value}>
              {format.label} -{' '}
              {format.value === inputExtension
                ? t('converter.settings.sameFormatSuffix', {
                    defaultValue: 'same format - recompress',
                  })
                : format.description}
            </option>
          ))}
        </select>

        {isSameFormat && (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-400">
            {t('converter.settings.sameFormatNotice', {
              defaultValue:
                'Recompressing to the same format re-encodes the file. It can shrink the file, but never improves quality.',
            })}
          </p>
        )}
      </div>

      {/* Quality */}
      <div className="mb-6">
        <label className="label-themed">
          {t('converter.settings.quality', { defaultValue: 'Quality' })}
        </label>
        <div className="space-y-2">
          {qualityOptions.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                settings.quality === option.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-secondary-200 dark:border-secondary-600 hover:bg-secondary-50 dark:hover:bg-secondary-700'
              }`}
            >
              <input
                type="radio"
                name="quality"
                value={option.value}
                checked={settings.quality === option.value}
                onChange={(e) =>
                  onChange({
                    ...settings,
                    quality: e.target.value as ConversionSettingsData['quality'],
                  })
                }
                className="w-4 h-4 text-primary-600 focus:ring-primary-500"
              />
              <div className="flex-1">
                <span className="font-medium text-secondary-900 dark:text-white">
                  {t(option.labelKey, { defaultValue: option.labelDefault })}
                </span>
                <span className="ml-2 text-sm text-secondary-500 dark:text-secondary-400">
                  ({t(option.hintKey, { defaultValue: option.hintDefault })})
                </span>
                {option.recommended && (
                  <span className="ml-2 text-xs bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded">
                    {t('common.recommended', { defaultValue: 'Recommended' })}
                  </span>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Options - only available in local mode (GPU processing on device) */}
      {isLocalMode() && (
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.preserveMetadata}
              onChange={(e) => onChange({ ...settings, preserveMetadata: e.target.checked })}
              className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
            />
            <span className="text-secondary-700 dark:text-secondary-300">
              {mediaType === 'audio'
                ? t('converter.settings.preserveMetadataAudio', {
                    defaultValue: 'Preserve metadata (artist, album, etc.)',
                  })
                : t('converter.settings.preserveMetadataVideo', {
                    defaultValue: 'Preserve metadata (title, creation date, etc.)',
                  })}
            </span>
          </label>

          {/* Audio-only: the video endpoint takes no normalize_audio parameter,
              so showing this for a video would be a control that does nothing. */}
          {mediaType === 'audio' && (
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.normalizeAudio}
                onChange={(e) => onChange({ ...settings, normalizeAudio: e.target.checked })}
                className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
              />
              <span className="text-secondary-700 dark:text-secondary-300">
                {t('converter.settings.normalizeAudio', { defaultValue: 'Normalize audio volume' })}
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
});

export default ConversionSettings;
